import React, { useMemo } from 'react';

/* External */
import { connect, useSelector } from 'react-redux';
import { compose } from 'redux';
import { Grid, Typography, Card, CardContent, List } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

/* Project */
import Item from 'screens/Dispatches/components/ItemList/components/Item';
import CustomPagination from 'commons/components/CustomPagination';
import SkeletonList from 'commons/components/SkeletonList';
import { dispatchesActions } from 'screens/Dispatches/reducer';
import { truthty, clone } from 'utils/functions';
import { useIsMobileDevice } from 'utils/hooks/isMobileDevice';
import connectSaga from 'utils/saga';
import { actionProps } from 'utils/reduxActions';
import { Header } from 'commons/components/DataTable/Header';
import saga from '../../saga';

function ItemList(props) {
  const { t } = useTranslation();
  const { classes, objects, actions, inputSearch, headerList } = props;

  const isMobileDevice = useIsMobileDevice();

  const dispatchList = useSelector((state) => state.dispatches.dispatchList);
  const pagination = useSelector((state) => state.dispatches.pagination);
  const loadingDispatches = useSelector((state) => state.dispatches.loadingDispatches);

  const dispatches = useMemo(() => {
    return Object.values(objects.dispatches).filter((d) => dispatchList.includes(d.id));
  }, [dispatchList, objects.dispatches]);

  const handleChangeRowsPerPage = (e) => {
    actions.paginationControls(inputSearch, { ...pagination, rowsPerPage: e.target.value });
  };

  const handleChangePage = (e, val) => {
    actions.paginationControls(inputSearch, { ...pagination, page: val });
  };

  const clients = useMemo(
    () => [
      '',
      ...Object.values(objects.clients)
        .map((c) => ({ id: c.id, name: c.entity().name }))
        .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1)),
    ],
    [objects.clients],
  );

  const suppliers = useMemo(
    () => [
      '',
      ...Object.values(objects.suppliers)
        .map((s) => ({ id: s.id, name: s.entity().name }))
        .sort((a, b) => (a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1)),
    ],
    [objects.suppliers],
  );

  const sortedDispatches = useMemo(
    () => [...dispatches].sort((a, b) => b.number - a.number),
    [dispatches],
  );

  return (
    <Grid item xs={12}>
      <Card className={classes.root}>
        <CardContent>
          {truthty(dispatches) ? (
            <Grid container spacing={3} className={classes.container}>
              <Grid item xs={12}>
                {loadingDispatches ? (
                  <SkeletonList times={pagination.rowsPerPage} />
                ) : (
                  <List>
                    <Header headerList={headerList} isMobileDevice={isMobileDevice} />
                    {sortedDispatches.map((dispatch) => (
                      <Item
                        key={`dispatch-${dispatch.id}`}
                        cDispatch={dispatch}
                        classes={classes}
                        suppliers={suppliers}
                        clients={clients}
                        headerList={headerList}
                        isMobileDevice={isMobileDevice}
                        objects={objects}
                      />
                    ))}
                  </List>
                )}
              </Grid>
              <CustomPagination
                rowsPerPage={pagination.rowsPerPage}
                page={pagination.page}
                count={pagination.count}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleChangePage={handleChangePage}
                loading={loadingDispatches}
              />
            </Grid>
          ) : (
            <Grid container item xs={12} alignItems="center" justifyContent="center">
              <Typography variant="h6" component="h2">
                {t('No dispatches')}
              </Typography>
            </Grid>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}

const withSaga = connectSaga({ key: 'sagaDispatches', saga });

const withConnect = connect(
  undefined,
  actionProps(clone(clone.OBJECT, { paginationControls: dispatchesActions.paginationControls })),
);

export default compose(withConnect)(withSaga(ItemList));
